import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { projectMetaKeys } from '@/hooks/meta/useProjectMeta';
import * as storiesService from '../supabase/stories';
import type { Story, StoryWithRelations } from '../supabase/stories';
import { storyFields } from '@/lib/field-sets';
import { supabase } from '../supabase/supabase';

// Define cache times - using the same aggressive caching as other queries
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = DAY_IN_MS * 30; // 30 days stale time
const GC_TIME = DAY_IN_MS * 60; // 60 days garbage collection

/**
 * Hook to fetch all stories for a project
 * @param projectId The project ID to fetch stories for
 * @param options Additional options for the query
 */
export function useStoriesByProject(
  projectId: string | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: queryKeys.stories.byProject(projectId || ''),
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required');
      return storiesService.getStoriesByProject(projectId);
    },
    enabled: !!projectId && (options.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to fetch a single story by ID
 * @param storyId The story ID to fetch
 * @param options Additional options for the query
 */
export function useStory<T extends boolean = false>(
  storyId: string | undefined,
  options: { enabled?: boolean; includeFull?: T } = {} as any
) {
  const { includeFull = false as T, ...restOptions } = options;

  return useQuery({
    queryKey: includeFull
      ? queryKeys.stories.full(storyId || '')
      : queryKeys.stories.detail(storyId || ''),
    queryFn: async () => {
      if (!storyId) throw new Error('Story ID is required');
      return storiesService.getStoryById(storyId, includeFull);
    },
    enabled: !!storyId && (restOptions.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to fetch just the name of a story
 * @param storyId The story ID to fetch
 * @param options Additional options for the query
 */
export function useStoryName(
  storyId: string | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: queryKeys.stories.detail(storyId || ''),
    queryFn: async () => {
      if (!storyId) throw new Error('Story ID is required');

      const { data, error } = await supabase
        .from('stories')
        .select('name')
        .eq('id', storyId)
        .single();

      if (error) throw error;
      return data as { name: string };
    },
    enabled: !!storyId && (options.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to fetch the current story for a project
 * @param projectId The project ID to fetch the current story for
 * @param options Additional options for the query
 */


/**
 * Hook to create a new story
 */
export function useCreateStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newStory: {
      project_id: string;
      name: string;
      plotline?: string;
      genre?: string;
      status?: 'draft' | 'in_progress' | 'completed';
    }) => {
      return storiesService.createStory(newStory);
    },
    onSuccess: (data) => {
      // Update the stories list for this project
      queryClient.invalidateQueries({
        queryKey: queryKeys.stories.byProject(data.project_id),
      });

      // Add the new story to the cache
      queryClient.setQueryData(
        queryKeys.stories.detail(data.id),
        data
      );
    },
  });
}

/**
 * Hook to update a story
 */
export function useUpdateStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Pick<Story, 'id'> & Partial<Omit<Story, 'id'>>) => {
      return storiesService.updateStory(id, updates);
    },
    onSuccess: (data) => {
      // Update the story detail in cache
      queryClient.setQueryData(
        queryKeys.stories.detail(data.id),
        data
      );

      // Also update the full story data if it's cached
      queryClient.invalidateQueries({
        queryKey: queryKeys.stories.full(data.id),
      });

      // Invalidate the project stories list
      queryClient.invalidateQueries({
        queryKey: queryKeys.stories.byProject(data.project_id),
      });

      // Invalidate all current story queries if this might be the current story
      queryClient.invalidateQueries({
        queryKey: ['currentStory', data.project_id],
        exact: false,
      });
    },
  });
}

/**
 * Hook to delete a story
 */
export function useDeleteStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, project_id }: Pick<Story, 'id' | 'project_id'>) => {
      await storiesService.deleteStory(id);
      return { id, project_id };
    },
    onSuccess: ({ id, project_id }) => {
      // Remove the story from the cache
      queryClient.removeQueries({
        queryKey: queryKeys.stories.detail(id),
      });
      queryClient.removeQueries({
        queryKey: queryKeys.stories.full(id),
      });

      // Invalidate the stories list for this project
      queryClient.invalidateQueries({
        queryKey: queryKeys.stories.byProject(project_id),
      });

      // Invalidate the project if this was the current story
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.detail(project_id),
      });

      // Invalidate all current story queries
      queryClient.invalidateQueries({
        queryKey: ['currentStory', project_id],
        exact: false,
      });
    },
  });
}

/**
 * Hook to set a story as the current story for a project
 */
export function useSetCurrentStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ project_id, story_id }: { project_id: string; story_id: string }) => {
      const project = await storiesService.setCurrentStory(project_id, story_id);
      return { project_id, story_id, project };
    },
    onSuccess: ({ project_id }) => {
      // Update the project in cache
      queryClient.invalidateQueries({
        queryKey: queryKeys.projects.detail(project_id),
      });
      // Update the project meta cache
      queryClient.invalidateQueries({
        queryKey: projectMetaKeys.detail(project_id),
      });

      // Signal that the current story has changed
      queryClient.invalidateQueries({
        queryKey: queryKeys.stories.byProject(project_id),
      });

      // Invalidate all current story queries for this project
      queryClient.invalidateQueries({
        queryKey: ['currentStory', project_id],
        exact: false,
      });
    },
  });
}
